import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Divider,
  useTheme
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Assignment as ProjectIcon,
  Description as QuoteIcon,
  Settings as ServiceIcon,
  Payment as PaymentIcon,
  Receipt as InvoiceIcon,
  Notifications as NotificationsIcon,
  VpnKey as LicenceIcon
} from '@mui/icons-material';

const Sidebar = ({ open, onToggle, isMobile }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  const menuItems = [
    { id: 'dashboard', label: 'Έσοδα', icon: <DashboardIcon />, path: '/' },
    { id: 'clients', label: 'Πελάτες', icon: <PeopleIcon />, path: '/clients' },
    { id: 'projects', label: 'Έργα', icon: <ProjectIcon />, path: '/projects' },
    { id: 'quotes', label: 'Προσφορές', icon: <QuoteIcon />, path: '/quotes' },
    { id: 'services', label: 'Υπηρεσίες', icon: <ServiceIcon />, path: '/services' },
    { id: 'invoices', label: 'Τιμολόγια', icon: <InvoiceIcon />, path: '/invoices' },
    { id: 'payments', label: 'Πληρωμές', icon: <PaymentIcon />, path: '/payments' },
    { id: 'licences', label: 'Άδειες', icon: <LicenceIcon />, path: '/licences' },
    { id: 'notifications', label: 'Ειδοποιήσεις', icon: <NotificationsIcon />, path: '/notifications' }
  ];

  const handleItemClick = (path) => {
    navigate(path);
    if (isMobile) {
      onToggle();
    }
  };

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ 
        p: 2, 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        borderBottom: `1px solid ${theme.palette.divider}`
      }}>
        <Box
          component="img"
          src={`${process.env.PUBLIC_URL}/main_logo.png`}
          alt="BillMaster Logo"
          sx={{
            width: 40,
            height: 40,
            borderRadius: 1,
            objectFit: 'contain'
          }}
        />
        {open && (
          <Typography variant="h6" sx={{ 
            color: theme.palette.primary.main,
            fontWeight: 'bold'
          }}>
            BillMaster
          </Typography>
        )}
      </Box>

      {/* Menu */}
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        <List sx={{ p: 1 }}>
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path || 
                           (item.path !== '/' && location.pathname.startsWith(item.path));
            
            return (
              <ListItem key={item.id} disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  onClick={() => handleItemClick(item.path)}
                  sx={{
                    borderRadius: 1,
                    backgroundColor: isActive ? theme.palette.primary.main : 'transparent',
                    color: isActive ? 'white' : theme.palette.text.primary,
                    '&:hover': {
                      backgroundColor: isActive 
                        ? theme.palette.primary.dark 
                        : theme.palette.action.hover,
                    },
                    minHeight: 48,
                  }}
                >
                  <ListItemIcon sx={{ 
                    color: isActive ? 'white' : theme.palette.text.secondary,
                    minWidth: open ? 40 : 'auto',
                    mr: open ? 1 : 0
                  }}>
                    {item.icon}
                  </ListItemIcon>
                  {open && (
                    <ListItemText 
                      primary={item.label}
                      primaryTypographyProps={{
                        fontSize: '14px',
                        fontWeight: isActive ? 600 : 400
                      }}
                    />
                  )}
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Box>

      {/* Footer */}
      {open && (
        <>
          <Divider />
          <Box sx={{ 
            p: 2, 
            textAlign: 'center',
            color: theme.palette.text.secondary,
            fontSize: '12px'
          }}>
            © 2024 BillMaster
          </Box>
        </>
      )}
    </Box>
  );

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'permanent'}
      open={isMobile ? open : true}
      onClose={onToggle}
      sx={{
        width: isMobile ? 240 : (open ? 240 : 60),
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: isMobile ? 240 : (open ? 240 : 60),
          boxSizing: 'border-box',
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          overflowX: 'hidden',
          position: 'relative',
          height: '100vh'
        },
      }}
      ModalProps={{
        keepMounted: true,
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default Sidebar;